import { ModelIdSymbol } from '../constants.js';
import PersonalNote from '../personal-note/index.js';
import { createIsInstance } from '../utils.js';
class PersonalNoteSet {
    _items;
    constructor(notes) {
        this._items = new Map();
        if (notes) {
            for (const item of notes) {
                const note = coerceToPersonalNote(item);
                this._items.set(String(note), note);
            }
        }
    }
    static fromArray(arr) {
        return arr == null ? PersonalNoteSet.EMPTY : new PersonalNoteSet(arr);
    }
    static get [ModelIdSymbol]() {
        return 'PersonalNoteSet';
    }
    static isInstance = createIsInstance(PersonalNoteSet);
    static [Symbol.hasInstance](instance) {
        return this.isInstance(instance);
    }
    static EMPTY = new PersonalNoteSet();
    add(input) {
        const notes = coerceToArray(input);
        if (notes.length === 0)
            return this;
        const candidates = PersonalNoteSet.fromArray(notes);
        const replaced = new Set();
        const toAdd = [];
        [...candidates].forEach((candidate) => {
            const conflict = this.get(candidate);
            if (!conflict) {
                toAdd.push(candidate);
                return;
            }
            if (!conflict.equals(candidate)) {
                toAdd.push(candidate);
                replaced.add(conflict);
            }
        });
        if (toAdd.length === 0)
            return this;
        const finalCopy = [...[...this].filter((note) => !replaced.has(note)), ...toAdd];
        return PersonalNoteSet.fromArray(finalCopy);
    }
    clone() {
        return PersonalNoteSet.fromArray([...this]);
    }
    equals(otherSet) {
        if (this === otherSet)
            return true;
        if (this.size !== otherSet.size)
            return false;
        return [...this].every((note) => {
            const candidate = otherSet.get(note);
            return candidate?.equals(note);
        });
    }
    get(item) {
        const txId = typeof item === 'string' ? item : item.txId;
        if (!txId) {
            throw new Error('expected txId or Object/PersonalNote with property txId');
        }
        return this._items.get(txId);
    }
    has(item) {
        return this._items.has(String(item));
    }
    toJSON() {
        return [...this].map((item) => item.toJSON());
    }
    update(items) {
        const updated = items.map((item) => {
            const existingItem = this.get(item);
            return existingItem ? existingItem.update(item) : coerceToPersonalNote(item);
        });
        return this.add(updated);
    }
    [Symbol.iterator]() {
        return this._items.values();
    }
    get size() {
        return this._items.size;
    }
}
const coerceToPersonalNote = (obj) => obj instanceof PersonalNote ? obj : PersonalNote.fromJSON(obj);
const coerceToArray = (obj) => (Array.isArray(obj) ? obj : [obj].flat());
export default PersonalNoteSet;
